import { roundDollars } from './money';

/**
 * The web's copy of the server's invoice math (backend/src/modules/billing/invoice-lines.ts and
 * billable-owners.ts), React-free so the backend's invoice-math-weld.spec.ts can run the same
 * fixtures through both. The server stays authoritative for saved totals; this drives the preview
 * and the card-charge prefill, which must not disagree with the saved invoice.
 */

export interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  category?: string;
  // BILL-DISC: optional per-line discount. When present, `total` is the
  // POST-discount amount (qty*unitPrice − discountAmount). Mirrors the backend
  // BillingLineItem shape — the server recomputes discountAmount/total anyway.
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  discountAmount?: number;
  discountReason?: string;
}

// BILL-DISC: invoice-level discount, applied to the post-line-item-discount
// subtotal BEFORE tax (and before any co-ownership split on the backend).
export interface InvoiceDiscount {
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  discountReason?: string;
}


export const SERVICE_CATEGORIES = ['procedure', 'appointment', 'other'];
export const PRODUCT_CATEGORIES = ['medication', 'vaccination'];

// Recompute a line's discountAmount + total from its qty/unitPrice/discount.
// Mirrors backend normalizeLineItemDiscounts (invoice-lines.ts); invoice-math-weld.spec.ts checks it.
export function applyLineDiscount(item: LineItem): LineItem {
  const base = roundDollars((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0));
  if (!item.discountType || !item.discountValue || item.discountValue <= 0) {
    const { discountType, discountValue, discountAmount, discountReason, ...rest } = item;
    return { ...rest, total: base };
  }
  const raw = item.discountType === 'percentage'
    ? (base * item.discountValue) / 100
    : item.discountValue;
  // Math.max(base, 0): on a negative line (courtesy credit) the naive [0, base]
  // clamp inverts and zeroes the credit line — mirror of the backend guard.
  const discountAmount = roundDollars(Math.min(Math.max(raw, 0), Math.max(base, 0)));
  return { ...item, discountAmount, total: roundDollars(base - discountAmount) };
}

// Mirrors backend computeInvoiceDiscountAmount (invoice-lines.ts); welded like the rest.
export function computeInvoiceDiscountAmount(
  subtotal: number,
  discount?: InvoiceDiscount | null,
): number {
  if (!discount || !discount.discountValue || discount.discountValue <= 0) return 0;
  const raw = discount.discountType === 'percentage'
    ? (subtotal * discount.discountValue) / 100
    : discount.discountValue;
  // Math.max(subtotal, 0): same negative-base clamp guard as applyLineDiscount.
  return roundDollars(Math.min(Math.max(raw, 0), Math.max(subtotal, 0)));
}

export function getTaxableSubtotal(lineItems: LineItem[], taxMode: string): number {
  if (taxMode === 'NONE') return 0;
  if (taxMode === 'ALL' || !taxMode) {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  }
  const taxableCategories = taxMode === 'SERVICES_ONLY' ? SERVICE_CATEGORIES : PRODUCT_CATEGORIES;
  return lineItems
    .filter(item => taxableCategories.includes(item.category || 'other'))
    .reduce((sum, item) => sum + item.total, 0);
}

// BILL-DISC: the invoice-level discount reduces the taxable base
// proportionally so tax is charged on what the client actually pays.
// Mirrors backend calculateTax (invoice-lines.ts); welded like the rest.
export function calculateTax(
  lineItems: LineItem[],
  taxRate: number,
  taxMode: string,
  invoiceDiscountAmount = 0,
): number {
  if (taxRate <= 0) return 0;
  let taxable = getTaxableSubtotal(lineItems, taxMode);
  if (invoiceDiscountAmount > 0) {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    if (subtotal > 0) {
      taxable *= Math.max(0, 1 - invoiceDiscountAmount / subtotal);
    }
  }
  return roundDollars(taxable * (taxRate / 100));
}

// Owners with an explicit 0% share carry no billing responsibility: the backend
// (resolveBillableOwners) excludes them from invoice splits, so a 100/0 horse bills
// as ONE invoice. All-zero anomaly: bill everyone, never nobody.
export function billableOwnersOf<T extends { ownershipPercentage?: number | null }>(
  owners: T[] | undefined | null,
): T[] {
  const all = Array.isArray(owners) ? owners : [];
  const billable = all.filter((o) => o?.ownershipPercentage !== 0);
  return billable.length ? billable : all;
}
